/**
 * アンカー（索引 Issue #498 の段階 1。設計文書 §1・§3）。
 *
 * アンカー＝**ソースが提供する「時刻を持つ点」**（再現エンジンのイベント）。帯の時刻は**アンカーの時刻そのもの**（`start = anchor.ms`。頭のずれは Issue #519 で廃止）。
 *
 * 文書に残るのは**人が動かしたアンカーだけ**（`doc.anchors[key] = { ms }`）。
 * 動かしていないアンカーの時刻は、開くたびにソースと整列（段階 2 の `align`）が決め直す。
 *
 * **人が置くアンカーの制約は並び順だけ**: 直前のアンカーより前には出せず、後ろで人が置いたアンカーは越えられない。
 * 逆転する移動は編集操作の側で無効にして元の位置に戻す（拒否の理由を返すのは `planAnchorMoves`）。
 *
 * ここは**書く側（文書）と検める側（解決済みの一覧）を分けてある**: 検めは `[{ key, ms, index, touched }]` の
 * 純関数、書き込みは文書の関数。段階 3 のコマンドが 2 つを繋いで `{ ok, reason }` にする。
 */
import { normalizeAnchorOverride, readDocument } from './document';

// ---------------------------------------------------------------- 文書（書く側）
/** 上書きを変える（`null` のキーは外す。空になったらキーごと消す） */
export function setAnchor(doc, key, patch = {}) {
  const d = readDocument(doc);
  const merged = { ...(d.anchors[key] ?? {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v == null) delete merged[k]; else merged[k] = v;
  }
  const next = normalizeAnchorOverride(merged);
  const anchors = { ...d.anchors };
  if (next) anchors[key] = next; else delete anchors[key];
  return { ...d, anchors };
}

export const clearAnchor = (doc, key) => setAnchor(doc, key, { ms: null, hidden: null });

/** まとめて動かす（検めは `planAnchorMoves` で先に済ませておくこと） */
export function moveAnchors(doc, moves = []) {
  let d = readDocument(doc);
  for (const m of moves) {
    if (!m?.key || !Number.isFinite(m.ms)) continue;
    d = setAnchor(d, m.key, { ms: Math.max(0, Math.round(m.ms)) });
  }
  return d;
}

// ---------------------------------------------------------------- 解決済みの一覧（検める側）
/**
 * そのアンカーを動かせる範囲。
 * @param anchors 整列後の一覧 `[{ key, ms, index, touched }]`（index 順）
 * @returns `{ min, max }`（`max` は `Infinity` になりうる）。キーが無ければ null
 */
function anchorMoveRange(anchors = [], key) {
  const i = anchors.findIndex((a) => a.key === key);
  if (i < 0) return null;
  // 下限は直前のアンカー。ただし**帯が無く人も触っていないアンカーは飛ばす** ——
  // 整列（align.js の後ろ向きのパス）がそこまで引き戻すので、下限にはならない
  let min = 0;
  for (let j = i - 1; j >= 0; j -= 1) {
    const a = anchors[j];
    if (a.ordering === false) continue;
    if (a.touched || a.hasObjects !== false) { min = Math.max(min, a.ms); break; }
  }
  let max = Number.POSITIVE_INFINITY;
  for (let j = i + 1; j < anchors.length; j += 1) {
    if (anchors[j].touched) { max = anchors[j].ms; break; }
  }
  return { min, max };
}

function canMoveAnchor(anchors = [], key, ms) {
  const range = anchorMoveRange(anchors, key);
  if (!range) return { ok: false, reason: 'そのアンカーがありません', range: null };
  if (!Number.isFinite(ms)) return { ok: false, reason: '時刻が不正です', range };
  if (ms < range.min || ms > range.max) {
    const max = Number.isFinite(range.max) ? `${Math.round(range.max)} ms` : '制限なし';
    return { ok: false, reason: `そこへは動かせません（${Math.round(range.min)} ms 〜 ${max}）。並びは入れ替えられません`, range };
  }
  return { ok: true, reason: null, range };
}

/**
 * まとめて動かす計画。**1 つでも並びが逆転するなら全部やめる**（半分だけ動くと、どれが動いたか分からないため）。
 * 検めは移動前の一覧に対して 1 件ずつ行い、**同時に動かすほかのアンカーは除いて**範囲を出す（同時に動かす者同士の
 * 順序は見ない）—— 同じ量だけずらす用途では相対順は保たれるので、これで足りる。除かないと、くっついて動く組
 * （motion と effect。Issue #644）の後ろが人の触ったアンカーのとき、前の 1 つが「後ろを越える」と断られる
 */
export function planAnchorMoves(anchors = [], moves = []) {
  const moving = new Set(moves.map((m) => m?.key));
  for (const m of moves) {
    const others = anchors.filter((a) => a.key === m?.key || !moving.has(a.key));
    const res = canMoveAnchor(others, m?.key, m?.ms);
    if (!res.ok) return { ok: false, reason: res.reason, moves: [] };
  }
  return { ok: true, reason: null, moves: moves.map((m) => ({ key: m.key, ms: m.ms })) };
}
